// ICS 204 Assignment List
import React, { useCallback, useMemo, useState } from 'react';
import ICSWidget from '../ICSWidget';
import { getIncidentCardTitle } from '../../USCGPage';
import {
  useWorkAssignments,
  ResourceNeeds,
} from 'components/IAP/hooks/workAssignmentHooks';
import {
  PlannedResource,
  WorkAssignmentListAttachmentCreateBody,
  useWorkAssignmentListAttachments,
  useUpdateWorkAssignmentListAttachment,
  useCreateWorkAssignmentListAttachment,
} from 'components/IAP/hooks/assignmentListHooks';
import { useIncidentPermissions } from 'components/IAP/hooks/incidentHooks';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { assignmentListColumns } from './columns';
import { createAssignmentListTableData } from './tableData';
import { ListFilter } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import type { Row } from '@tanstack/react-table';
import { NULL_VALUE } from '@/constants/displayConstants';
import AddWalaDialog from './AddWalaDialog';
import AssignmentListItem from './AssignmentListItem';

export type WorkGroupResourceNeed = ResourceNeeds & {
  workAssignmentId: string;
  workAssignmentName: string;
};

export type Status =
  | 'awaiting_review'
  | 'draft'
  | 'in_progress'
  | 'completed'
  | 'incomplete';

export type ProposedStatusOption =
  | null
  | 'Draft'
  | 'Assigned'
  | 'Submitted Complete for Review'
  | 'Reported Incomplete for Review'
  | 'Completed'
  | 'Incomplete';

export type SimplifiedWorkAssignment = {
  id: string;
  type: string;
  name: string;
  description: string;
  workGroupName?: string;
  workGroupId?: string;
  overheadPosition?: string;
  specialInstructions?: string;
  reportingLocation?: string;
  timestamp?: string;
};

type AssignmentListItemData = {
  walaId: string | null;
  workGroupId: string;
  workGroup: string;
  branch: string;
  division: string;
  walaGroup: string;
  stagingArea: string;
  proposedStatus: ProposedStatusOption;
  actualStatus: string | null;
  sectionChief?: string;
  branchDirector?: string;
  divisionGroupSupervisor?: string;
  workAssignments: SimplifiedWorkAssignment[];
  workGroupResourceNeeds: WorkGroupResourceNeed[];
  plannedResources: PlannedResource[] | null;
  medicalCommunication?: string;
  evacuationCommunication?: string;
  otherCommunication?: string;
  specialInstructions?: string;
};

type AssignmentListProps = {
  periodNumber: number;
};

type FilterColumnKey =
  | 'workGroup'
  | 'branch'
  | 'division'
  | 'walaGroup'
  | 'stagingArea'
  | 'proposedStatus';

const FILTERABLE_COLUMNS: { key: FilterColumnKey; label: string }[] = [
  { key: 'workGroup', label: 'Assignee' },
  { key: 'branch', label: 'Branch' },
  { key: 'division', label: 'Division' },
  { key: 'walaGroup', label: 'Group' },
  { key: 'stagingArea', label: 'Staging Area' },
  { key: 'proposedStatus', label: 'Status' },
];

export const mapProposedStatus = (
  proposedStatus: Status | null,
  actualStatus: Status | null
): ProposedStatusOption => {
  switch (proposedStatus) {
    case 'draft':
      return 'Draft';
    case 'in_progress':
      return 'Assigned';
    case 'completed':
      if (actualStatus === 'awaiting_review') {
        return 'Submitted Complete for Review';
      }
      return 'Completed';
    case 'incomplete':
      if (actualStatus === 'awaiting_review') {
        return 'Reported Incomplete for Review';
      }
      return 'Incomplete';
    default:
      return null; // Default to null if unknown
  }
};

const useAddWalaDialog = () => {
  const [isWalaDialogOpen, setIsWalaDialogOpen] = useState(false);
  const [is215Mode, setIs215Mode] = useState(false);

  const openWalaDialogForIcs215 = () => {
    setIsWalaDialogOpen(true);
    setIs215Mode(true);
  };

  const openWalaDialogForScratch = () => {
    setIsWalaDialogOpen(true);
    setIs215Mode(false);
  };

  const closeWalaDialog = () => {
    setIsWalaDialogOpen(false);
  };

  return {
    isWalaDialogOpen,
    openWalaDialogForIcs215,
    openWalaDialogForScratch,
    closeWalaDialog,
    is215Mode,
  };
};

const AssignmentList: React.FC<AssignmentListProps> = ({ periodNumber }) => {
  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;

  const { data: workAssignments } = useWorkAssignments();
  const { data: workAssignmentListAttachments } =
    useWorkAssignmentListAttachments();
  const updateWala = useUpdateWorkAssignmentListAttachment();
  const createWala = useCreateWorkAssignmentListAttachment();

  const data = createAssignmentListTableData({
    workAssignments,
    workAssignmentListAttachments,
    periodNumber,
  });

  const [tableSearch, setTableSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<
    Record<FilterColumnKey, string[]>
  >({
    workGroup: [],
    branch: [],
    division: [],
    walaGroup: [],
    stagingArea: [],
    proposedStatus: [],
  });

  const toggleColumnFilterValue = (key: FilterColumnKey, value: string) => {
    setColumnFilters((prev) => {
      const current = prev[key];
      const next = current.includes(value)
        ? current.filter((existing) => existing !== value)
        : [...current, value];
      return { ...prev, [key]: next };
    });
  };

  const filteredData = useMemo(() => {
    const query = tableSearch.trim().toLowerCase();

    return data.filter((item) => {
      const matchesSearch =
        !query ||
        item.workGroup?.toLowerCase().includes(query) ||
        item.proposedStatus?.toLowerCase().includes(query);
      if (!matchesSearch) return false;

      return FILTERABLE_COLUMNS.every(({ key }) => {
        const selectedValues = columnFilters[key];
        if (selectedValues.length === 0) return true;
        const value = item[key];
        return typeof value === 'string' && selectedValues.includes(value);
      });
    });
  }, [data, tableSearch, columnFilters]);

  const upsertWala = useCallback(
    (
      row: Row<AssignmentListItemData>,
      values: Omit<
        WorkAssignmentListAttachmentCreateBody,
        'workGroupId' | 'periodNumber'
      >
    ) => {
      // eslint-disable-next-line unused-imports/no-unused-vars
      const { proposedStatus, plannedResources, walaId, ...rest } =
        row.original;

      if (walaId) {
        updateWala.mutate({
          walaId,
          plannedResources: plannedResources ?? undefined,
          ...rest,
          ...values,
        });
        return;
      }

      createWala.mutate({
        plannedResources: plannedResources ?? undefined,
        periodNumber,
        ...rest,
        ...values,
      });
    },
    [createWala, periodNumber, updateWala]
  );

  const {
    isWalaDialogOpen,
    openWalaDialogForIcs215,
    openWalaDialogForScratch,
    closeWalaDialog,
    is215Mode,
  } = useAddWalaDialog();

  return (
    <div data-testid="assignmentList-widget">
      <div className="tw:my-4 tw:flex tw:justify-between">
        <ICSWidget.HeaderLeft
          iconName="assignment"
          iconColor="primary"
          title={getIncidentCardTitle('assignmentList')}
          subtitle=""
        />

        <DropdownMenu>
          <DropdownMenuTrigger render={<Button variant="outline" size="sm" />}>
            + Create{' '}
            <i className="material-symbols-outlined" aria-hidden="true">
              keyboard_arrow_down
            </i>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault();
                openWalaDialogForIcs215();
              }}
            >
              + Create from ICS-215
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault();
                openWalaDialogForScratch();
              }}
            >
              + Create from Scratch
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <AddWalaDialog
          needsExistingAssignments={is215Mode}
          periodNumber={periodNumber}
          isDialogOpen={isWalaDialogOpen}
          closeDialog={() => closeWalaDialog()}
        />
      </div>

      <div className="tw:mb-2 tw:flex tw:items-center tw:space-x-2!">
        <Popover>
          <PopoverTrigger render={<Button variant="outline" size="sm" />}>
            <ListFilter /> Filter
          </PopoverTrigger>
          <PopoverContent
            className="tw:max-h-96 tw:w-64 tw:overflow-y-auto"
            align="start"
          >
            <div className="tw:grid tw:gap-4">
              {FILTERABLE_COLUMNS.map(({ key, label }) => {
                const options = Array.from(
                  new Set(
                    data
                      .map((item) => item[key])
                      .filter(
                        (value): value is string =>
                          typeof value === 'string' && value.trim().length > 0
                      )
                  )
                );

                return (
                  <div key={key} className="tw:grid tw:gap-2">
                    <span className="tw:text-xs tw:font-semibold tw:text-muted-foreground tw:uppercase">
                      {label}
                    </span>
                    <div className="tw:grid tw:gap-1">
                      {options.length === 0 ? (
                        <span className="tw:text-xs tw:text-muted-foreground">
                          {NULL_VALUE}
                        </span>
                      ) : (
                        options.map((option) => (
                          <label
                            key={option}
                            className="tw:flex! tw:items-center tw:gap-2 tw:text-sm"
                          >
                            <Checkbox
                              checked={columnFilters[key].includes(option)}
                              onCheckedChange={() =>
                                toggleColumnFilterValue(key, option)
                              }
                            />
                            {option}
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
        <Input
          placeholder="Search assignments..."
          value={tableSearch}
          onChange={(event) => setTableSearch(event.target.value)}
          className="tw:max-w-sm"
        />
      </div>

      <DataTable
        columns={assignmentListColumns}
        data={filteredData}
        renderSubComponent={({ row }) => (
          <AssignmentListItem
            row={row}
            upsertWala={upsertWala}
            canEditCards={canEditCards}
          />
        )}
      />
    </div>
  );
};

export default AssignmentList;
